import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Mail, Trash2 } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const STATUSES = ["nuova", "in corso", "confermata", "rifiutata"];
const STATUS_CLS = {
  nuova: "border-red-600 text-red-500",
  "in corso": "border-amber-700 text-amber-500",
  confermata: "border-emerald-800 text-emerald-500",
  rifiutata: "border-neutral-700 text-neutral-500",
};

const fmt = (iso) =>
  new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

export default function BookingsAdmin({ headers, onUnauthorized }) {
  const [rows, setRows] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/bookings`, { headers: headers() });
      setRows(data);
    } catch (err) {
      if (err.response?.status === 401) onUnauthorized();
      else toast("Impossibile caricare le richieste");
    }
  }, [headers, onUnauthorized]);
  useEffect(() => {
    load();
  }, [load]);

  const setStatus = async (r, status) => {
    try {
      const { data } = await axios.patch(`${API}/api/admin/bookings/${r.id}`, { status }, { headers: headers() });
      setRows((rows) => rows.map((x) => (x.id === r.id ? data : x)));
      toast("Stato aggiornato", { description: `${r.venue} → ${status}` });
    } catch {
      toast("Aggiornamento non riuscito");
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Eliminare la richiesta di ${r.name} (${r.venue})?`)) return;
    try {
      await axios.delete(`${API}/api/admin/bookings/${r.id}`, { headers: headers() });
      setRows((rows) => rows.filter((x) => x.id !== r.id));
      toast("Richiesta eliminata");
    } catch {
      toast("Eliminazione non riuscita");
    }
  };

  return (
    <div data-testid="admin-bookings-list" className="space-y-3">
      {rows?.length === 0 && (
        <p className="border border-dashed border-neutral-800 p-10 text-center text-sm text-neutral-500">
          Nessuna richiesta ancora. Arriveranno qui (e via email) dal modulo del sito.
        </p>
      )}
      {rows?.map((r) => (
        <article key={r.id} data-testid="admin-booking-card" className="border border-neutral-800 bg-[#0D0D0D] p-5">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <p className="font-mono-tac text-[10px] uppercase tracking-[0.3em] text-neutral-500">{fmt(r.created_at)}</p>
              <h3 className="font-serif-logo mt-1 text-lg font-bold tracking-wide text-neutral-100">
                {r.venue} <span className="text-neutral-500">— {r.city}</span>
              </h3>
              <p className="mt-1 text-sm text-neutral-300">
                {r.name} · <a className="text-red-500 hover:underline" href={`mailto:${r.email}`}>{r.email}</a>
                {r.phone && <span className="text-neutral-400"> · {r.phone}</span>}
                {r.event_date && <span className="text-neutral-400"> · {r.event_date}</span>}
              </p>
              {r.message && <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-neutral-400">{r.message}</p>}
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <select
                data-testid="admin-booking-status"
                value={r.status}
                onChange={(e) => setStatus(r, e.target.value)}
                className={`h-9 border bg-black/40 px-2 font-mono-tac text-[10px] uppercase tracking-[0.2em] focus:outline-none ${STATUS_CLS[r.status]}`}
              >
                {STATUSES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
              <a href={`mailto:${r.email}?subject=${encodeURIComponent(`Linea Cremisi — ${r.venue}`)}`} aria-label="Rispondi" className="flex h-9 w-9 items-center justify-center border border-neutral-800 text-neutral-400 hover:border-red-700 hover:text-red-500">
                <Mail className="h-4 w-4" />
              </a>
              <button data-testid="admin-booking-delete" onClick={() => remove(r)} aria-label="Elimina richiesta" className="flex h-9 w-9 items-center justify-center border border-neutral-800 text-neutral-600 hover:border-red-700 hover:text-red-500">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
          <p className="mt-3 font-mono-tac text-[10px] uppercase tracking-[0.2em] text-neutral-600">
            Email alla band: {r.notified_at ? "inviata" : "non inviata"}
          </p>
        </article>
      ))}
    </div>
  );
}
